// Ore fields generated with the map (§26 F3).
//
// The board used to start empty — every unit of material was earned from a
// capture, which is the right coupling while there is one material and the
// wrong one the moment there are three: stone and the specials have no wartime
// source at all, so a bill charged in cobble is a bill nobody can pay.
//
// **A field is a patch, not a square.** The first version scattered single
// tiles and it read as *piles*, which is what a heap is — the thing a deposit
// is supposed to be the opposite of. A field is three to five contiguous
// tiles, visibly one deposit of one material, each tile holding its own deep
// finite charge: a pawn drills the tile it stands on until it is dry and steps
// to the next one, and the patch shrinks from wherever it is being worked.
// Tight and deep rather than broad and shallow, so a vein is land worth
// fighting over instead of floor the board is painted with.
//
// Generated in `src/engine/ai/` rather than in the rules because it is a *choice of
// board* like a formation or an anchor, and it rides to the reducer as
// ordinary command data — which is what makes a run replay exactly.

import { fileOf, KING_DIRS, rankOf, ROOK_DIRS } from "src/engine/rules/board";
import type { DepositRow } from "src/engine/rules/industry/state";
import {
  COAL,
  DIAMOND,
  ELECTRUM,
  FUEL,
  type MaterialLine,
  METAL,
  SPECIAL,
  STONE,
  THIN
} from "src/engine/rules/materials";
import { streamFor } from "src/engine/rules/random";
import { RULES } from "src/engine/rules/rules";

/**
 * The stream deposits are drawn from.
 *
 * **Its own, never another draw off a camp's `roll`.** Slipping a draw into
 * the middle of that sequence would change every existing seed's formations,
 * facings and anchors as a side effect of adding ore — the argument
 * `PAIR_OFFER_STREAM` makes.
 *
 * **A number nobody else uses, not merely a number that reads as new.** An
 * opening board draws at `(seed, 0)` and a camp at `(seed, stage)`, which is
 * the grain half the salts in the repo already use — so a shared salt is a
 * shared *stream*, and two features would draw the same numbers with nothing
 * saying so. Clear of the sides and `PATROL_SALT` (0–15), `OFFER_STREAM` (7)
 * and its candidate offsets, the deck's 11, 31 (once `STYLE_STREAM`, retired
 * — leave it unused), `PAIR_STREAM` (41), `PAIR_OFFER_STREAM` (51),
 * `TRAINER_SALT` (61/67/71/73), the watch page's `WATCH_STREAM` (71),
 * `MARK_SALT` (79) and `LOOSE_STREAM` (9000+). It continues the
 * 31/41/51/61/71 ladder.
 */
export const DEPOSIT_STREAM = 81;

/**
 * How many salts deposits reserve, so several openings in one stage can each
 * draw their own numbers.
 *
 * A run opens the board more than once a stage — a camp lands, and an
 * objective reveals a region — and `streamFor(seed, stage, DEPOSIT_STREAM)` is
 * one stream, so two openings on one stage would generate the same field
 * twice. The **block** rather than a number, because the count is a property
 * of the level: `81 + 1 + regionIndex` for a reveal, 81 itself for a camp.
 * 81..127 is clear — the highest salt anything else uses below `LOOSE_STREAM`
 * is `MARK_SALT` at 79.
 */
export const DEPOSIT_SALTS = 47;

/**
 * Where the **due-roll** lives inside the block, clear of the generation salts.
 *
 * "Does this opening bring a field" and "what does the field look like" are two
 * questions and must be two streams. Sharing one is subtler than it sounds and
 * was shipped: the due-roll drew first, then `rollDeposits` built a *fresh*
 * generator on the same salt, so the generator's own first draw — the square
 * the patch grows from — was the **same number** the due-roll had just
 * compared against `depositSpawnChance`. Every field that passed the roll
 * therefore grew from the bottom three quarters of the open list, for ever.
 * The same shared-stream sin this block exists to prevent, one level in.
 *
 * Twenty-four, which splits the block in half: generation takes 0..23 (a camp
 * and up to twenty-three regions) and the due-rolls take 24..47 alongside them.
 */
export const DUE_OFFSET = 24;

/**
 * The lines a board's *workhorse* fields are dealt from, in order, **metal
 * first**.
 *
 * Dealt rather than rolled: at two or three fields a board, independent coin
 * tosses leave a quarter of boards with no stone at all, and a board with no
 * stone is a board where nothing can be built. Metal leads because pieces are
 * what a run is played with — a stone-first opening is a run that cannot build
 * one — and carving it first also gives it first pick of the ground.
 *
 * The specials are not in it: they are placed as their own micro-patches,
 * because a field of electrum the size of an iron one is not scarce. Nor is
 * coal: it rides **beside every iron vein** (`COAL_BESIDE_IRON`), since metal
 * cannot be raised a rung without it (§31 M2) and a coal-less board is a
 * board where nothing past a pawn can be forged.
 */
const WORKHORSES: readonly MaterialLine[] = [METAL, STONE];

/**
 * A coal vein comes with every iron one (§31 M2), at a workhorse's depth: a
 * rung of metal costs a unit of coal, so coal is needed about as often as iron.
 */
export const COAL_BESIDE_IRON = true;

/** Chebyshev, the same measure territory and a camp's clearance already use. */
const apart = (a: number, b: number): number =>
  Math.max(Math.abs(fileOf(a) - fileOf(b)), Math.abs(rankOf(a) - rankOf(b)));

/**
 * Ground far enough from every army to be worth walking to, in **sorted square
 * order** — Map and insertion order are what two clients agree on by luck.
 */
export function depositGround(
  ground: readonly number[],
  keepAway: readonly number[],
  clearance: number
): number[] {
  return [...ground]
    .sort((a, b) => a - b)
    .filter((sq) => keepAway.every((piece) => apart(sq, piece) >= clearance));
}

/**
 * Grow one blob of `want` tiles out of `pool`, starting at `seed`.
 *
 * Rank the whole frontier by squared distance from the origin, break ties by
 * how much of the blob already touches the square so notches fill before
 * spikes grow, and break those by square. That is what makes the shape a disc
 * rather than a finger — walking the frontier in square order grows a column
 * straight down the board.
 *
 * **No randomness at all.** Where the blob starts is a draw; what it grows
 * into is a function of the ground, so two clients cannot disagree about it
 * and a patch is the same shape at any point in a replay.
 */
export function growPatch(
  pool: ReadonlySet<number>,
  seed: number,
  want: number
): number[] {
  const taken = [seed];
  const inside = new Set([seed]);
  const away = (sq: number): number => {
    const df = fileOf(sq) - fileOf(seed);
    const dr = rankOf(sq) - rankOf(seed);
    return df * df + dr * dr;
  };

  while (taken.length < want) {
    // The whole frontier is measured before any of it is taken — the trap
    // `spreadGas` records, where taking neighbours in square order grew a
    // finger toward one corner instead of a disc.
    const touching = new Map<number, number>();
    for (const sq of inside) {
      // `KING_DIRS`, so a patch grows eight ways and comes out round. Never a
      // written-out stride: a square is a packed coordinate and the board's
      // own offsets are the only honest way to step between two.
      for (const step of KING_DIRS) {
        const adj = sq + step;
        if (!pool.has(adj) || inside.has(adj)) continue;
        touching.set(adj, (touching.get(adj) ?? 0) + 1);
      }
    }
    if (touching.size === 0) break;
    const next = [...touching.keys()].sort(
      (a, b) =>
        away(a) - away(b) ||
        // Fill the notches first: a tile the patch already surrounds is a
        // dent, and taking it smooths the edge rather than growing a spike.
        (touching.get(b) ?? 0) - (touching.get(a) ?? 0) ||
        a - b
    )[0];
    taken.push(next);
    inside.add(next);
  }
  return taken;
}

/**
 * How rich a **workhorse** tile is: richest where the patch grew from,
 * ordinary elsewhere. A special is flat — see `depositSpecialAmount`.
 *
 * Two tiers and no middle ring — a three-to-five tile blob never leaves the
 * seed's own ring on open ground, so a third tier is a dial nothing reads.
 */
function charge(tile: number, seed: number): number {
  return tile === seed ? RULES.depositAmountSeed : RULES.depositAmount;
}

/** An integer in `[low, high]` off one draw. */
const between = (roll: () => number, low: number, high: number): number =>
  low + Math.floor(roll() * (high - low + 1));

/**
 * Ground the board does not have yet, near enough to bring a field to.
 *
 * **Bridged orthogonally**, which is `touchesBoard`'s rule for a camp and is
 * about more than tidiness: a belt carries along `DIR_OFFSET`, so a lobe
 * attached only at a corner is ground no lane can ever reach — a field nobody
 * can haul from is not a field, it is scenery.
 *
 * Grown eight ways from there, like the patch it is for, and bounded: the pool
 * is what a blob of `want` could plausibly use, not every square off the edge
 * of the world.
 */
function lobeGround(
  position: { cells: ReadonlySet<number>; cellList: readonly number[] },
  keepAway: readonly number[],
  clearance: number,
  seedAt: number,
  want: number
): number[] {
  const lobe: number[] = [];
  const taken = new Set<number>();
  const frontier = new Set([seedAt]);
  const room = (sq: number): number => {
    let least = Infinity;
    for (const piece of keepAway) least = Math.min(least, apart(sq, piece));
    return least;
  };
  let clear = 0;

  // **The lobe opens the ground it crosses, not only the ground it uses**, and
  // it heads *outward*. The squares just off a camp's edge are inside the
  // clearance by construction — that is what clearance means, and measured,
  // no bridge point of the tight camps clears it — so a lobe of only-clear
  // squares starts nowhere. What is grown instead is a contiguous run of new
  // ground that always takes the square **furthest from the army**, so it
  // reaches away from the camp and the ore sits at the far end of it. A plain
  // breadth-first ball grows sideways along the camp's own edge and never gets
  // out of anybody's reach: measured, nine squares and not one of them clear.
  //
  // Ties by square, like every ordering here. It stops as soon as it holds
  // enough clear ground for the patch, and is capped, because a run should
  // grow a lobe rather than a continent.
  const cap = Math.max(want * 3, 12);

  /**
   * How much of the lobe this square already touches — `growPatch`'s notch
   * count, counted eight ways for `growPatch`'s reason.
   *
   * The lobe's own squares only, never the board's. Counting the camp's edge
   * would score the squares alongside it highest and grow the lobe *along* the
   * camp, which is the failure the outward drive above exists to prevent.
   */
  const hugs = (sq: number): number => {
    let n = 0;
    for (const step of KING_DIRS) if (taken.has(sq + step)) n++;
    return n;
  };

  while (frontier.size > 0 && lobe.length < cap && clear < want) {
    /**
     * **Furthest-out is a band, not a maximum, and that is the whole of it.**
     *
     * Taking the single furthest square walks a line one square wide: the
     * frontier's most distant member is always the tip's own forward
     * neighbour, which is one further out than the two beside it, so the tie
     * break never gets a say and the lobe is a tendril up to `cap` long.
     * Reported from play as ore arriving in long lines that extend the board
     * — and it is *both* symptoms, because the patch is grown out of the clear
     * lobe squares, so a lobe one wide can only hold a field one wide.
     *
     * `growPatch` solved this ten lines up and this never got it: rank by
     * reach, then fill the notches so the edge smooths instead of spiking.
     * The band is what lets the notch count matter at all — with an exact
     * maximum the forward square wins by one every time, for ever.
     *
     * One square of slack, which is the least that admits a neighbour of the
     * tip. Wider and the lobe starts eating sideways along the camp's edge,
     * where nothing clears the clearance.
     */
    let reach = -Infinity;
    for (const sq of frontier) reach = Math.max(reach, room(sq));
    let best = -1;
    for (const sq of frontier) {
      if (room(sq) < reach - 1) continue;
      if (best < 0 || hugs(sq) > hugs(best) || (hugs(sq) === hugs(best) && sq < best)) best = sq;
    }
    frontier.delete(best);
    taken.add(best);
    lobe.push(best);
    if (room(best) >= clearance) clear++;
    // **Cardinal, so every square of the lobe is orthogonally connected.** A
    // belt carries along `DIR_OFFSET`, so ground joined only at a corner is
    // ground no lane can ever run to — and `growth.ts` already argues the
    // shape half of this: ground is *built*, and a four-way push grows lobes
    // and inlets where an eight-way one pours out a rectangle.
    for (const step of ROOK_DIRS) {
      const adj = best + step;
      if (!taken.has(adj) && !position.cells.has(adj)) frontier.add(adj);
    }
  }
  return lobe;
}

/**
 * Where a lobe of new ground would join on: the squares just off the board's
 * own edge, in square order.
 */
function bridgePoints(position: {
  cells: ReadonlySet<number>;
  cellList: readonly number[];
}): number[] {
  const out = new Set<number>();
  for (const sq of position.cellList) {
    for (const step of ROOK_DIRS) {
      const adj = sq + step;
      if (!position.cells.has(adj)) out.add(adj);
    }
  }
  return [...out].sort((a, b) => a - b);
}

/** A field, and the ground it had to bring with it. */
export interface FieldPlan {
  rows: DepositRow[];
  /** Squares the board does not have yet. Empty when the field fitted. */
  ground: number[];
}

const NO_FIELD: FieldPlan = { rows: [], ground: [] };

/**
 * Fields for a piece of ground, away from the army it belongs to.
 *
 * Pure in its arguments, so the same run generates the same map twice and a
 * reload cannot re-roll it — `waveOptions`' rule, and the reason nothing about
 * a deposit is stored anywhere but the board.
 *
 * A **run's opening** gets one patch and a **scenario board** gets a share of
 * its clear ground, because the two are different sizes of question: a camp's
 * floor is twenty-odd squares and a padded board's margin is a hundred, and
 * one rule for both either buries a camp or leaves a board with a single seam.
 */
export function rollDeposits(
  seed: number,
  stage: number,
  ground: readonly number[],
  keepAway: readonly number[],
  options: {
    /** One patch, sized for a run, rather than a field per sixty squares. */
    camp?: boolean;
    /**
     * The board, when the field is allowed to **bring its own ground**.
     *
     * Without it a field is fitted into whatever clear squares happen to be
     * left, which is how a run's fields arrived late and small: a camp's floor
     * is mostly stood on, so the leftovers decided the size of the deposit. A
     * field is the point of the expansion, so the expansion is sized to it.
     */
    board?: { cells: ReadonlySet<number>; cellList: readonly number[] };
    /**
     * The line this field is made of, where the caller has an opinion.
     *
     * **The caller decides and the generator obeys.** A run spawns one field
     * at a time over a sequence of openings, so a coin toss per spawn is the
     * within-board quota's defect reintroduced across the run: measured, a
     * two-or-three-field run came out stoneless about one time in four.
     * `runStep` passes whichever line the board is short of; a caller with no
     * opinion gets metal, because pieces are what a run is played with.
     */
    line?: MaterialLine;
    /** Which salt inside the deposit block — see `DEPOSIT_SALTS`. */
    salt?: number;
    clearance?: number;
  } = {}
): FieldPlan {
  const clearance = options.clearance ?? RULES.depositClearance;
  const pool = new Set(depositGround(ground, keepAway, clearance));
  const roll = streamFor(seed, stage, DEPOSIT_STREAM + (options.salt ?? 0));
  const rows: DepositRow[] = [];
  const fresh: number[] = [];

  /** Take one patch out of the pool, so two fields never share a tile. */
  const carve = (want: number, line: number, grade: number, start?: number): void => {
    const open = [...pool].sort((a, b) => a - b);
    if (open.length === 0) return;
    const from =
      start !== undefined && pool.has(start)
        ? start
        : open[Math.min(open.length - 1, Math.floor(roll() * open.length))];
    for (const tile of growPatch(pool, from, want)) {
      pool.delete(tile);
      // A special is priced flat off its own dial: deepening a workhorse vein
      // must not multiply the electrum and diamond supply behind anyone's back.
      const units = line === SPECIAL ? RULES.depositSpecialAmount : charge(tile, from);
      rows.push([tile, line, grade, units] as const);
    }
  };

  if (options.camp) {
    // **The size is chosen, then the room is found.** Where a lobe can be
    // grown the patch aims at the range somebody picked; where it cannot — a
    // scenario board, whose shape is authored — it is whatever the clear
    // ground holds, and below `depositTilesMin` there is no field at all.
    const want = options.board
      ? between(roll, RULES.depositTilesMin, RULES.depositTilesMax)
      : Math.min(RULES.depositTilesMax, pool.size);
    // **Iron brings its coal** (§31 M2): the room is found for both veins.
    const metal = (options.line ?? METAL) === METAL && COAL_BESIDE_IRON;
    const coalWant = metal ? RULES.depositTilesMin : 0;
    // **Room first, then the field.** Where the existing clear ground cannot
    // hold the patch, the field brings a lobe of new squares rather than being
    // trimmed to fit — which is the whole of iteration 2: generate ground for
    // the deposit, not a deposit for the leftover ground.
    if (pool.size < want + coalWant && options.board) {
      const bridges = bridgePoints(options.board);
      if (bridges.length > 0) {
        const at = bridges[Math.min(bridges.length - 1, Math.floor(roll() * bridges.length))];
        // **Ground for the whole patch, not for the shortfall.** Sizing the
        // lobe by what the old pool was missing assumes the two are one place,
        // and they are not: the old clear ground is interior and the lobe
        // hangs off the edge, so a patch grown on the lobe cannot reach across
        // to spend the difference. Measured at the shortfall: a field that
        // asked for eight got five. What the interior pocket is still good for
        // is the special micro-patch, which draws pool-wide.
        for (const sq of lobeGround(options.board, keepAway, clearance, at, want + coalWant)) {
          fresh.push(sq);
          // Ground for all of it, tiles only where the clearance allows: the
          // squares nearest the bridge are inside somebody's reach and are
          // there to be walked over rather than dug.
          if (keepAway.every((piece) => apart(sq, piece) >= clearance)) pool.add(sq);
        }
      }
    }
    if (pool.size < RULES.depositTilesMin) return NO_FIELD;
    // **Where a lobe was grown, the field starts on it** — the clear lobe
    // square furthest from the army, ties by square, and never a draw.
    //
    // A mixed pool is **disconnected**: the old clear ground is interior and
    // the lobe hangs off the board's edge, so `growPatch` cannot cross from
    // one to the other. Starting in the old component exhausts it and stops —
    // a small field, which is the complaint this iteration answers, *and* the
    // whole lobe opened with no ore on it, which is dead new ground. Roughly a
    // coin toss on any camp with a handful of interior squares. "The ore sits
    // at the far end of it" is the intent; this is it enforced.
    const onLobe = fresh.filter((sq) => pool.has(sq));
    let head: number | undefined;
    for (const sq of onLobe) {
      const room = (at: number): number =>
        keepAway.reduce((least, piece) => Math.min(least, apart(at, piece)), Infinity);
      if (head === undefined || room(sq) > room(head) || (room(sq) === room(head) && sq < head)) {
        head = sq;
      }
    }
    // **Metal where the caller has no opinion**, never a toss: pieces are what
    // a run is played with, so a stone-first opening is a run that cannot
    // build one.
    carve(Math.min(want, pool.size), options.line ?? METAL, 0, head);
    // Coal beside it, grown from wherever the pool still has room — after the
    // iron, so the iron keeps first pick of the lobe.
    if (metal && pool.size > 0) carve(Math.min(coalWant, pool.size), FUEL, COAL);
    // A run's opening carries a special now and then rather than always: one
    // that always paid electrum would make the specials a schedule.
    if (roll() < RULES.depositSpecialChance) {
      const grade = roll() < 2 / 3 ? ELECTRUM : DIAMOND;
      carve(between(roll, RULES.depositSpecialMin, RULES.depositSpecialMax), SPECIAL, grade);
    }
    // **The whole lobe, not only the tiles.** It is one contiguous run from
    // the bridge, so opening a subset of it is how an island gets made — and
    // the squares that carry no ore are the approach to the ones that do.
    //
    // **Nothing at all if no tile landed on it**: ground opened for a field
    // that did not arrive is board nobody asked for, and it is the second half
    // of the disconnection above.
    const used = new Set(rows.map(([sq]) => sq));
    const opened = fresh.some((sq) => used.has(sq)) ? fresh : [];
    return { rows: tiered(rows.sort((a, b) => a[0] - b[0]), roll), ground: opened.sort((a, b) => a - b) };
  }
  if (pool.size === 0) return NO_FIELD;

  const patches = Math.min(RULES.depositPatches, 1 + Math.floor(pool.size / RULES.depositEvery));
  // **Lines dealt in order, metal first** — see `WORKHORSES`. A board with room
  // for one field gets iron; the second is cobble; nothing is rolled.
  for (let i = 0; i < patches; i++) {
    const line = WORKHORSES[i % WORKHORSES.length];
    carve(between(roll, RULES.depositTilesMin, RULES.depositTilesMax), line, 0);
    // Every iron vein brings a coal vein (§31 M2).
    if (line === METAL && COAL_BESIDE_IRON) {
      carve(between(roll, RULES.depositTilesMin, RULES.depositTilesMax), FUEL, COAL);
    }
  }

  // One special micro-patch an opening — the whole supply of the queen's coin
  // and the rocket's fuel, two electrum seams to every diamond one.
  {
    const grade = roll() < 2 / 3 ? ELECTRUM : DIAMOND;
    carve(between(roll, RULES.depositSpecialMin, RULES.depositSpecialMax), SPECIAL, grade);
  }

  // Sorted on the way out, for the same reason the pool is: what a log carries
  // must not depend on the order a draw happened to visit.
  return { rows: tiered(rows.sort((a, b) => a[0] - b[0]), roll), ground: [] };
}

/**
 * **Mixed and tiered** (§31 D1, the design doc's Deposits): every tile that is
 * not a quarry is cobble plus its line's material, at a tier drawn per tile
 * off `RULES.depositTierOdds` — thin, concentrated or rich. Stone stays a
 * quarry, cobble only. Drawn **after** every square is chosen, over the rows
 * in square order, so a tier costs the field's shape nothing: the same seed
 * carves the same ground it did before tiers existed.
 */
function tiered(rows: readonly DepositRow[], roll: () => number): DepositRow[] {
  return rows.map(([sq, line, grade, amount]) =>
    line === STONE ? ([sq, line, grade, amount] as const) : ([sq, line, grade, amount, tierFor(roll())] as const)
  );
}

/** The tier a draw in [0, 1) lands on. */
export function tierFor(draw: number): number {
  let edge = 0;
  for (let i = 0; i < RULES.depositTierOdds.length; i++) {
    edge += RULES.depositTierOdds[i];
    if (draw < edge) return THIN + i;
  }
  return THIN + RULES.depositTierOdds.length - 1;
}
